import type { RevisionOperation, VisitSnapshot } from './types'

const sectionKeys = ['details', 'areas', 'categoryProgress', 'quickChecks', 'openingResponses', 'closing'] as const

export const recordKey = (recordType: RevisionOperation['recordType'], recordId: string) => `${recordType}:${recordId}`
const changed = (before: unknown, after: unknown) => JSON.stringify(before) !== JSON.stringify(after)

function operation(
  visit: VisitSnapshot,
  recordType: RevisionOperation['recordType'],
  recordId: string,
  action: RevisionOperation['action'],
  payload: unknown,
): RevisionOperation {
  return {
    id: crypto.randomUUID(),
    visitId: visit.id,
    recordType,
    recordId,
    action,
    expectedRevision: visit.recordRevisions[recordKey(recordType, recordId)] ?? 0,
    payload,
    author: visit.contributorName || 'Unspecified contributor',
    createdAt: new Date().toISOString(),
    attempts: 0,
  }
}

function entityOperations<T extends { id: string }>(
  current: VisitSnapshot,
  recordType: 'observation' | 'opportunity' | 'evidence',
  before: T[],
  after: T[],
) {
  const operations: RevisionOperation[] = []
  const beforeById = new Map(before.map((item) => [item.id, item]))
  const afterById = new Map(after.map((item) => [item.id, item]))
  for (const item of after) {
    const previous = beforeById.get(item.id)
    if (!previous || changed(previous, item)) {
      operations.push(operation(current, recordType, item.id, 'upsert', item))
    }
  }
  for (const item of before) {
    if (!afterById.has(item.id)) operations.push(operation(current, recordType, item.id, 'delete', null))
  }
  return operations
}

export function prepareVisitCommit(
  current: VisitSnapshot,
  mutated: VisitSnapshot,
): { visit: VisitSnapshot; operations: RevisionOperation[] } {
  const next: VisitSnapshot = {
    ...mutated,
    recordRevisions: current.recordRevisions ?? {},
    revision: current.revision + 1,
    updatedAt: new Date().toISOString(),
  }
  const operations: RevisionOperation[] = []
  for (const key of sectionKeys) {
    if (changed(current[key], next[key])) {
      operations.push(operation(current, 'section', key, 'upsert', next[key]))
    }
  }
  if (
    current.templateId !== next.templateId
    || current.templateVersion !== next.templateVersion
    || changed(current.templateSnapshot, next.templateSnapshot)
  ) {
    operations.push(operation(current, 'section', 'template', 'upsert', {
      templateId: next.templateId,
      templateVersion: next.templateVersion,
      templateSnapshot: next.templateSnapshot,
    }))
  }
  operations.push(
    ...entityOperations(current, 'observation', current.observations, next.observations),
    ...entityOperations(current, 'opportunity', current.opportunities, next.opportunities),
    ...entityOperations(current, 'evidence', current.evidence, next.evidence),
  )
  return { visit: next, operations }
}

export function applyServerRecord(
  visit: VisitSnapshot,
  operationValue: RevisionOperation,
  serverRecord: unknown,
  serverRevision: number,
) {
  const key = recordKey(operationValue.recordType, operationValue.recordId)
  let next = { ...visit, recordRevisions: { ...visit.recordRevisions, [key]: serverRevision } }
  if (operationValue.recordType === 'section') {
    if (operationValue.recordId === 'template') {
      next = {
        ...next,
        ...(serverRecord as Pick<VisitSnapshot, 'templateId' | 'templateVersion' | 'templateSnapshot'>),
      }
    } else {
      next = { ...next, [operationValue.recordId]: serverRecord }
    }
    return next
  }
  const collectionKey = operationValue.recordType === 'observation'
    ? 'observations'
    : operationValue.recordType === 'opportunity'
      ? 'opportunities'
      : 'evidence'
  const collection = next[collectionKey] as Array<{ id: string }>
  const without = collection.filter((item) => item.id !== operationValue.recordId)
  return {
    ...next,
    [collectionKey]: serverRecord == null ? without : [...without, serverRecord],
  } as VisitSnapshot
}

export function applyAcceptedRevisions(
  visit: VisitSnapshot,
  accepted: Array<{ recordType: RevisionOperation['recordType']; recordId: string; revision: number }>,
) {
  const revisions = { ...visit.recordRevisions }
  accepted.forEach((item) => {
    revisions[recordKey(item.recordType, item.recordId)] = item.revision
  })
  return { ...visit, recordRevisions: revisions }
}
